import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Copy, RefreshCw, Download, Monitor, ExternalLink, Save, ShieldCheck } from "lucide-react";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const pct = (v) => `${Math.round(v || 0)}%`;
const barTone = (v) => (v >= 90 ? "bg-destructive" : v >= 70 ? "bg-amber-500" : "bg-emerald-500");

export default function AgentSetup() {
  const [token, setToken] = useState("");
  const [mesh, setMesh] = useState("");
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDevices = () => api.get("/agents/devices").then((r) => setDevices(r.data)).catch(() => {});

  useEffect(() => {
    Promise.all([
      api.get("/agents/enroll-token").then((r) => setToken(r.data.token)).catch(() => {}),
      api.get("/agents/mesh").then((r) => setMesh(r.data.mesh_url || "")).catch(() => {}),
      loadDevices(),
    ]).finally(() => setLoading(false));
    const t = setInterval(loadDevices, 20000);
    return () => clearInterval(t);
  }, []);

  const rotate = async () => {
    if (!window.confirm("¿Regenerar el token? Los agentes actuales dejarán de reportar hasta actualizarlos.")) return;
    const { data } = await api.post("/agents/enroll-token");
    setToken(data.token); toast.success("Token regenerado");
  };
  const saveMesh = async () => { await api.put("/agents/mesh", { mesh_url: mesh }); toast.success("MeshCentral guardado"); };
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copiado"); };
  const installUrl = `${BACKEND}/api/agent/install?token=${token}`;

  return (
    <OSLayout title="OMNIA Endpoint Agent">
      <div className="space-y-6" data-testid="agent-page">
        <div>
          <div className="overline text-navy/40 mb-2">OMNIA Enterprise OS™ · IT</div>
          <h2 className="font-editorial text-4xl tracking-tight">OMNIA Endpoint Agent</h2>
          <p className="mt-2 text-navy/50 max-w-2xl">Instala el agente en cada equipo para que Device Health, Network Status y Asset Inventory se actualicen solos. La instalación masiva la hace tu área de TI vía Intune, GPO o Jamf.</p>
        </div>

        {loading ? <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <>
            {/* Inscripción */}
            <div className="card-surface p-5 space-y-4" data-testid="agent-enroll">
              <div className="flex items-center gap-2 overline text-navy/40"><ShieldCheck className="w-4 h-4" /> Token de inscripción de la empresa</div>
              <div className="flex gap-2">
                <input readOnly value={token} className="flex-1 bg-navy/[0.03] border border-navy/10 rounded-lg px-3 py-2 font-mono text-sm text-navy/80" data-testid="agent-token" />
                <button onClick={() => copy(token)} className="btn btn-ghost" data-testid="agent-token-copy"><Copy className="w-4 h-4" /></button>
                <button onClick={rotate} className="btn btn-ghost" data-testid="agent-token-rotate"><RefreshCw className="w-4 h-4" /> Regenerar</button>
              </div>
              <a href={installUrl} target="_blank" rel="noreferrer" className="btn btn-primary" data-testid="agent-download"><Download className="w-4 h-4" /> Descargar agente (Python)</a>
              <div className="text-sm text-navy/60 space-y-1">
                <p className="text-navy/80 font-medium">Instalación (Windows / Mac / Linux):</p>
                <pre className="bg-navy/[0.03] rounded-lg p-3 text-xs overflow-x-auto">pip install psutil requests
python omnia_agent.py   # déjalo corriendo (servicio / tarea programada)</pre>
                <p className="text-xs text-navy/40">Para despliegue masivo: empújalo con Intune/GPO (Windows), Jamf (Mac) o tu RMM. Reporta cada 5 min.</p>
              </div>
            </div>

            {/* MeshCentral */}
            <div className="card-surface p-5 space-y-3" data-testid="agent-mesh">
              <div className="overline text-navy/40">Soporte remoto (MeshCentral)</div>
              <p className="text-sm text-navy/50">Pega la URL de tu servidor MeshCentral auto-hospedado. Aparecerá como botón "Conectar" para el control remoto.</p>
              <div className="flex gap-2">
                <input value={mesh} onChange={(e) => setMesh(e.target.value)} placeholder="https://mesh.tuempresa.com" className="flex-1 bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet" data-testid="agent-mesh-input" />
                <button onClick={saveMesh} className="btn btn-primary" data-testid="agent-mesh-save"><Save className="w-4 h-4" /></button>
                {mesh && <a href={mesh} target="_blank" rel="noreferrer" className="btn btn-ghost" data-testid="agent-mesh-open"><ExternalLink className="w-4 h-4" /> Abrir</a>}
              </div>
              <details className="text-sm" data-testid="mesh-guide">
                <summary className="cursor-pointer text-violet hover:text-navy">¿Cómo montar MeshCentral? (guía paso a paso)</summary>
                <ol className="mt-2 list-decimal pl-5 space-y-1 text-navy/60">
                  <li>Renta un servidor Linux (Ubuntu 22.04, 2 vCPU / 4 GB) con un dominio, p. ej. <code>mesh.tuempresa.com</code>.</li>
                  <li>Instala Node.js 18+: <code>curl -fsSL https://deb.nodesource.com/setup_18.x | sudo -E bash - && sudo apt install -y nodejs</code></li>
                  <li>Crea la carpeta e instala: <code>mkdir ~/meshcentral && cd ~/meshcentral && npm install meshcentral</code></li>
                  <li>Arranca una vez: <code>node node_modules/meshcentral --cert mesh.tuempresa.com</code> (obtiene TLS Let's Encrypt automático en el puerto 443).</li>
                  <li>Abre <code>https://mesh.tuempresa.com</code>, crea la cuenta admin y un "Device Group".</li>
                  <li>Descarga el "Mesh Agent" desde ese grupo e instálalo en los equipos (o empújalo con Intune/GPO/Jamf).</li>
                  <li>Déjalo como servicio: <code>sudo npm install -g pm2 && pm2 start node_modules/meshcentral/meshcentral.js && pm2 save && pm2 startup</code></li>
                  <li>Pega arriba <code>https://mesh.tuempresa.com</code> y guarda. Listo: el botón "Abrir/Conectar" abrirá la consola de control remoto.</li>
                </ol>
              </details>
            </div>

            {/* Dispositivos en vivo */}
            <div className="card-surface overflow-x-auto" data-testid="agent-devices">
              <div className="flex items-center gap-2 px-4 pt-4 overline text-navy/40"><Monitor className="w-4 h-4" /> Equipos reportando · {devices.length}</div>
              {devices.length === 0 ? (
                <div className="py-10 text-center text-navy/40 text-sm">Aún no hay equipos. Descarga e instala el agente con el token de arriba.</div>
              ) : (
                <table className="w-full text-sm mt-2">
                  <thead><tr className="border-b border-navy/10 text-navy/50 text-left"><th className="px-4 py-2">Equipo</th><th className="px-4 py-2">SO</th><th className="px-4 py-2">CPU</th><th className="px-4 py-2">Memoria</th><th className="px-4 py-2">Disco</th><th className="px-4 py-2">Estado</th><th className="px-4 py-2">Visto</th></tr></thead>
                  <tbody>
                    {devices.map((d) => (
                      <tr key={d.dev_key} className="border-b border-navy/5 last:border-0" data-testid={`agent-device-${d.hostname}`}>
                        <td className="px-4 py-2 text-navy/80">{d.hostname} {d.online ? <span className="badge badge-emerald ml-1">en línea</span> : <span className="badge badge-muted ml-1">offline</span>}</td>
                        <td className="px-4 py-2 text-navy/60">{d.os}</td>
                        {["cpu", "memoria", "almacenamiento"].map((k) => (
                          <td key={k} className="px-4 py-2"><div className="flex items-center gap-2"><div className="h-1.5 w-16 rounded-full bg-navy/10 overflow-hidden"><div className={`h-full rounded-full ${barTone(d[k])}`} style={{ width: pct(d[k]) }} /></div><span className="text-xs text-navy/50">{pct(d[k])}</span></div></td>
                        ))}
                        <td className="px-4 py-2"><span className={`badge ${d.estado === "critico" ? "badge-danger" : d.estado === "advertencia" ? "badge-amber" : "badge-emerald"}`}>{d.estado}</span></td>
                        <td className="px-4 py-2 text-navy/40 text-xs">{d.last_seen ? new Date(d.last_seen).toLocaleString("es-MX") : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </OSLayout>
  );
}
